import { apiClient } from "@/lib/api/client";
import type {
  ApiMessageResponse,
  BusinessApplicationPayload,
  BusinessCategoryDto,
  BusinessBillingInvoiceDto,
  BusinessDirectoryResponse,
  CatalogResponse,
  DepartmentCreatePayload,
  DepartmentDto,
  DepartmentUpdatePayload,
  ManagedBusinessOptionDto,
  MyBusinessMembershipDto,
  ProfileUpdatePayload,
  PublicBusinessProfileDto,
  ServiceCreatePayload,
  ServiceUpdatePayload,
  StaffInvitePayload,
  StaffInviteDetailDto,
  StaffLinkDto,
  StaffPermissionsPayload,
  StaffSelfRequestPayload,
} from "@/features/businesses/types";
import type { SwitchActiveContextResponse } from "@/features/auth/types";

export const getBusinessDirectory = async (params?: {
  q?: string;
  city?: string;
  category?: string;
}): Promise<BusinessDirectoryResponse> => {
  const response = await apiClient.get<BusinessDirectoryResponse>("/businesses/directory", { params });
  return response.data;
};

export const getPublicBusinessProfile = async (slug: string): Promise<PublicBusinessProfileDto> => {
  const response = await apiClient.get<PublicBusinessProfileDto>(`/businesses/public/${slug}`);
  return response.data;
};

export const getBusinessCategories = async (): Promise<BusinessCategoryDto[]> => {
  const response = await apiClient.get<BusinessCategoryDto[]>("/businesses/categories");
  return response.data;
};

export const getManagedBusinessProfile = async (businessId: number): Promise<PublicBusinessProfileDto> => {
  const response = await apiClient.get<PublicBusinessProfileDto>(`/businesses/${businessId}/profile`);
  return response.data;
};

export const getOwnedBusinesses = async (): Promise<ManagedBusinessOptionDto[]> => {
  const response = await apiClient.get<ManagedBusinessOptionDto[]>("/businesses/manage/owned");
  return response.data;
};

export const getMyBusinessMemberships = async (): Promise<MyBusinessMembershipDto[]> => {
  const response = await apiClient.get<MyBusinessMembershipDto[]>("/businesses/manage/mine");
  return response.data;
};

export const activateBusinessManagementContext = async (businessId: number): Promise<SwitchActiveContextResponse> => {
  const response = await apiClient.post<SwitchActiveContextResponse>(`/businesses/${businessId}/activate-management-context`);
  return response.data;
};

export const applyForBusiness = async (payload: BusinessApplicationPayload): Promise<ManagedBusinessOptionDto> => {
  const response = await apiClient.post<ManagedBusinessOptionDto>("/businesses/apply", payload);
  return response.data;
};

export const getBusinessStaff = async (businessId: number): Promise<StaffLinkDto[]> => {
  const response = await apiClient.get<StaffLinkDto[]>(`/businesses/${businessId}/staff`);
  return response.data;
};

export const getBusinessBillingInvoices = async (businessId: number): Promise<BusinessBillingInvoiceDto[]> => {
  const response = await apiClient.get<BusinessBillingInvoiceDto[]>(`/businesses/${businessId}/billing/invoices`, {
    params: { business_id: businessId },
  });
  return response.data;
};

export const inviteBusinessStaff = async (businessId: number, payload: StaffInvitePayload): Promise<StaffLinkDto> => {
  const response = await apiClient.post<StaffLinkDto>(`/businesses/${businessId}/staff/invite`, payload);
  return response.data;
};

export const getBusinessBillingInvoicePdf = async (businessId: number, invoiceId: number): Promise<Blob> => {
  const response = await apiClient.get(`/businesses/${businessId}/billing/invoices/${invoiceId}/pdf`, {
    responseType: "blob",
  });
  return response.data as Blob;
};

export const getBusinessBillingInvoicesSummaryPdf = async (
  businessId: number,
  period: "day" | "week" | "month" | "year" | "all",
): Promise<Blob> => {
  const response = await apiClient.get(`/businesses/${businessId}/billing/invoices/export`, {
    params: { period },
    responseType: "blob",
  });
  return response.data as Blob;
};

export const payBusinessBillingInvoice = async (businessId: number, invoiceId: number): Promise<ApiMessageResponse> => {
  const response = await apiClient.post<ApiMessageResponse>(`/businesses/${businessId}/billing/invoices/${invoiceId}/pay`);
  return response.data;
};

export const requestBusinessStaffLink = async (businessId: number, payload: StaffSelfRequestPayload): Promise<StaffLinkDto> => {
  const response = await apiClient.post<StaffLinkDto>(`/businesses/${businessId}/staff/request`, payload);
  return response.data;
};

export const acceptBusinessStaffInvite = async (linkId: number): Promise<ApiMessageResponse> => {
  const response = await apiClient.post<ApiMessageResponse>(`/businesses/staff-links/${linkId}/accept`);
  return response.data;
};

export const getBusinessStaffInvite = async (linkId: number): Promise<StaffInviteDetailDto> => {
  const response = await apiClient.get<StaffInviteDetailDto>(`/businesses/staff-links/${linkId}`);
  return response.data;
};

export const removeBusinessStaffLink = async (linkId: number): Promise<ApiMessageResponse> => {
  const response = await apiClient.post<ApiMessageResponse>(`/businesses/staff-links/${linkId}/remove`);
  return response.data;
};

export const updateBusinessStaffPermissions = async (
  linkId: number,
  payload: StaffPermissionsPayload,
): Promise<StaffLinkDto> => {
  const response = await apiClient.patch<StaffLinkDto>(`/businesses/staff-links/${linkId}/permissions`, payload);
  return response.data;
};

export const getBusinessCatalog = async (businessId: number): Promise<CatalogResponse> => {
  const response = await apiClient.get<CatalogResponse>(`/businesses/${businessId}/catalog`);
  return response.data;
};

export const createBusinessDepartment = async (
  businessId: number,
  payload: DepartmentCreatePayload,
): Promise<ApiMessageResponse> => {
  const response = await apiClient.post<ApiMessageResponse>(`/businesses/${businessId}/departments`, payload);
  return response.data;
};

export const createBusinessService = async (
  businessId: number,
  payload: ServiceCreatePayload,
): Promise<ApiMessageResponse> => {
  const response = await apiClient.post<ApiMessageResponse>(`/businesses/${businessId}/services`, payload);
  return response.data;
};

export const updateBusinessService = async (
  businessId: number,
  serviceId: number,
  payload: ServiceUpdatePayload,
): Promise<PublicBusinessProfileDto["services"][number]> => {
  const response = await apiClient.patch<PublicBusinessProfileDto["services"][number]>(
    `/businesses/${businessId}/services/${serviceId}`,
    payload,
  );
  return response.data;
};

export const archiveBusinessService = async (
  businessId: number,
  serviceId: number,
): Promise<ApiMessageResponse> => {
  const response = await apiClient.delete<ApiMessageResponse>(`/businesses/${businessId}/services/${serviceId}`);
  return response.data;
};

export const updateBusinessDepartment = async (
  businessId: number,
  departmentId: number,
  payload: DepartmentUpdatePayload,
): Promise<DepartmentDto> => {
  const res = await apiClient.patch<DepartmentDto>(`/businesses/${businessId}/departments/${departmentId}`, payload);
  return res.data;
};

export const archiveBusinessDepartment = async (
  businessId: number,
  departmentId: number,
): Promise<ApiMessageResponse> => {
  const res = await apiClient.delete<ApiMessageResponse>(`/businesses/${businessId}/departments/${departmentId}`);
  return res.data;
};

export const updateBusinessProfile = async (
  businessId: number,
  payload: ProfileUpdatePayload,
): Promise<PublicBusinessProfileDto> => {
  const response = await apiClient.patch<PublicBusinessProfileDto>(`/businesses/${businessId}/profile`, payload);
  return response.data;
};

export const uploadBusinessLogo = async (
  businessId: number,
  file: File,
): Promise<{ ok: boolean; logo_path: string }> => {
  const form = new FormData();
  form.append("file", file);
  const res = await apiClient.post<{ ok: boolean; logo_path: string }>(
    `/businesses/${businessId}/logo`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return res.data;
};

export const uploadBusinessCover = async (
  businessId: number,
  file: File,
): Promise<{ ok: boolean; cover_path: string }> => {
  const form = new FormData();
  form.append("file", file);
  const res = await apiClient.post<{ ok: boolean; cover_path: string }>(
    `/businesses/${businessId}/cover`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return res.data;
};
